import React, { useState, useMemo } from 'react';
import { useFinance } from '../contexts/FinanceContext';
import { X, TrendingUp, TrendingDown, Minus, ChevronRight } from 'lucide-react';

const ForecastModal = ({ isOpen, onClose }) => {
  const { financeData } = useFinance();
  const [expandedMonth, setExpandedMonth] = useState(null);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const isPaymentActiveInMonth = (payment, targetDate) => {
    if (payment.useExactDates && payment.startDate && payment.endDate) {
      const startDate = new Date(payment.startDate);
      const endDate = new Date(payment.endDate);

      if (payment.installments === 0) {
        return targetDate >= startDate && targetDate <= endDate;
      }

      return targetDate >= startDate && targetDate <= endDate;
    }

    const paymentStartMonth = payment.startMonth || new Date().getMonth() + 1;
    const targetMonth = targetDate.getMonth() + 1;
    const targetYear = targetDate.getFullYear();
    const currentYear = new Date().getFullYear();

    if (payment.installments === 0) {
      return true;
    }

    if (payment.installments > 0) {
      let monthsSinceStart;
      if (targetYear === currentYear) {
        if (targetMonth >= paymentStartMonth) {
          monthsSinceStart = targetMonth - paymentStartMonth;
        } else {
          monthsSinceStart = (12 - paymentStartMonth) + targetMonth;
        }
      } else {
        const monthsInStartYear = 12 - paymentStartMonth + 1;
        const monthsInTargetYear = targetMonth;
        monthsSinceStart = monthsInStartYear + (targetYear - currentYear - 1) * 12 + monthsInTargetYear;
      }

      const remainingInstallments = payment.installments - monthsSinceStart;
      return monthsSinceStart >= 0 && remainingInstallments > 0;
    }

    return false;
  };

  const forecastData = useMemo(() => {
    if (!financeData.payments.length) return [];

    const now = new Date();
    const forecast = [];
    const currentMonth = now.getMonth() + 1;

    for (let i = 0; i < 12; i++) {
      const futureDate = new Date(now.getFullYear(), now.getMonth() + i, 1);
      let monthlyTotal = 0;
      const paymentsInMonth = [];

      financeData.payments.forEach(payment => {
        const isActiveInMonth = isPaymentActiveInMonth(payment, futureDate);

        if (isActiveInMonth) {
          monthlyTotal += payment.amount;
          paymentsInMonth.push(payment);
        }
      });

      const monthName = futureDate.toLocaleString('es-ES', {
        month: 'long',
        year: 'numeric'
      });

      forecast.push({
        month: monthName.charAt(0).toUpperCase() + monthName.slice(1),
        total: monthlyTotal,
        payments: paymentsInMonth,
        monthIndex: i
      });
    }

    return forecast;
  }, [financeData.payments]);

  const toggleMonthExpansion = (monthIndex) => {
    setExpandedMonth(expandedMonth === monthIndex ? null : monthIndex);
  };

  const getMonthDifference = (currentMonth, previousMonth) => {
    if (!previousMonth) return { difference: 0, type: 'neutral' };

    const diff = currentMonth.total - previousMonth.total;
    return {
      difference: diff,
      type: diff > 0 ? 'increase' : diff < 0 ? 'decrease' : 'neutral'
    };
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content animate-slide-up md:animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800">🔮 Previsión de Gastos</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        <div className="modal-body">
          <p className="text-gray-600 mb-4 text-sm md:text-base text-center">
            Estimación de gastos mensuales (sin alquiler). Toca un mes para ver detalles.
          </p>

          {forecastData.length === 0 ? (
            <div className="text-center py-8 md:py-12">
              <div className="text-5xl md:text-6xl mb-4">📊</div>
              <p className="text-gray-600">No hay pagos para generar una previsión.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {forecastData.map((month, index) => {
                const diff = getMonthDifference(month, forecastData[index - 1]);
                const isExpanded = expandedMonth === month.monthIndex;

                return (
                  <div key={month.monthIndex} className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      className={`w-full p-3 md:p-4 text-left transition-all duration-200 hover:bg-gray-50 ${isExpanded ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                        }`}
                      onClick={() => toggleMonthExpansion(month.monthIndex)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 md:gap-3 min-w-0">
                          <span className="font-semibold text-gray-800 text-sm md:text-base truncate">
                            {month.month}
                          </span>
                          {diff.type !== 'neutral' && (
                            <div className={`flex items-center gap-1 text-xs md:text-sm ${diff.type === 'increase' ? 'text-red-600' : 'text-green-600'
                              }`}>
                              {diff.type === 'increase' ? (
                                <TrendingUp className="w-3 h-3 md:w-4 md:h-4" />
                              ) : (
                                <TrendingDown className="w-3 h-3 md:w-4 md:h-4" />
                              )}
                              <span className="hidden sm:inline">
                                {formatCurrency(Math.abs(diff.difference))}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-lg md:text-xl font-bold text-blue-600">
                            {formatCurrency(month.total)}
                          </span>
                          <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''
                            }`} />
                        </div>
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="bg-gray-50 p-3 md:p-4 border-t border-gray-200 animate-fade-in">
                        {index === 0 ? (
                          <div className="text-center text-gray-600">
                            <h4 className="font-semibold mb-1 text-sm md:text-base">Mes base</h4>
                            <p className="text-xs md:text-sm">Referencia para calcular cambios futuros.</p>
                          </div>
                        ) : (
                          <div>
                            <h4 className="font-semibold mb-2 text-gray-800 text-sm md:text-base">
                              Cambio:
                              <span className={`ml-2 ${diff.type === 'increase' ? 'text-red-600' :
                                  diff.type === 'decrease' ? 'text-green-600' : 'text-gray-600'
                                }`}>
                                {diff.type === 'increase' ? '+' : diff.type === 'decrease' ? '-' : ''}
                                {formatCurrency(Math.abs(diff.difference))}
                              </span>
                            </h4>

                            {diff.type !== 'neutral' && (
                              <div className="space-y-1">
                                {diff.type === 'decrease' && forecastData[index - 1].payments
                                  .filter(prevPayment =>
                                    !month.payments.some(currentPayment =>
                                      currentPayment.id === prevPayment.id
                                    )
                                  )
                                  .map(payment => (
                                    <div key={payment.id} className="flex items-center gap-2 text-green-600 text-xs md:text-sm">
                                      <Minus className="w-3 h-3" />
                                      <span>Terminó <strong>{payment.name}</strong> (-{formatCurrency(payment.amount)})</span>
                                    </div>
                                  ))
                                }

                                {diff.type === 'increase' && month.payments
                                  .filter(currentPayment =>
                                    !forecastData[index - 1].payments.some(prevPayment =>
                                      prevPayment.id === currentPayment.id
                                    )
                                  )
                                  .map(payment => (
                                    <div key={payment.id} className="flex items-center gap-2 text-red-600 text-xs md:text-sm">
                                      <Minus className="w-3 h-3" />
                                      <span>Comenzó <strong>{payment.name}</strong> (+{formatCurrency(payment.amount)})</span>
                                    </div>
                                  ))
                                }
                              </div>
                            )}

                            {diff.type === 'neutral' && (
                              <p className="text-gray-600 text-center text-xs md:text-sm">
                                Sin cambios respecto al mes anterior.
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForecastModal;